import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Settings as SettingsIcon, Save, Download } from 'lucide-react';
import Button from '../../components/UI/Button';
import { toast } from 'react-toastify';
import { useAuthStore } from '../../stores/authStore';
import { isElectronApp } from '../../lib/isElectron';
import { getErrorMessage } from '../../lib/utils/errorMessage';

type Language = 'en' | 'ur';
type Theme = 'light' | 'dark' | 'system';
type FontSize = 'sm' | 'md' | 'lg';

const Settings: React.FC = () => {
  const { user, setUser } = useAuthStore();
  const [language, setLanguage] = useState<Language>('en');
  const [theme, setTheme] = useState<Theme>('system');
  const [fontSize, setFontSize] = useState<FontSize>('md');
  const [slipPrinter, setSlipPrinter] = useState<string>('');
  const [reportPrinter, setReportPrinter] = useState<string>('');
  const [printers, setPrinters] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [activeTab] = useState<'preferences'>('preferences');
  const [appVersion, setAppVersion] = useState<string>('');
  const [isDesktop, setIsDesktop] = useState(false);
  const [checkingUpdate, setCheckingUpdate] = useState(false);
  const manualUpdateCheck = useRef(false);
  // Mirror appVersion in a ref so the (mount-once) update listeners can read the
  // latest value without re-subscribing — re-subscribing duplicated listeners.
  const appVersionRef = useRef<string>('');

  // Load saved prefs at mount (local, user-specific)
  useEffect(() => {
    try {
      const prefs = JSON.parse(localStorage.getItem('hria-prefs') || '{}');
      if (prefs.reportLanguage) setLanguage(prefs.reportLanguage);
      if (prefs.theme) setTheme(prefs.theme);
      if (prefs.fontSize) setFontSize(prefs.fontSize);
      if (prefs.slipPrinter) setSlipPrinter(prefs.slipPrinter);
      if (prefs.reportPrinter) setReportPrinter(prefs.reportPrinter);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (user?.preferences) {
      const prefs = user.preferences as any;
      if (prefs.reportLanguage) setLanguage(prefs.reportLanguage);
      if (prefs.theme) setTheme(prefs.theme);
      if (prefs.fontSize) setFontSize(prefs.fontSize);
      if (prefs.slipPrinter) setSlipPrinter(prefs.slipPrinter);
      if (prefs.reportPrinter) setReportPrinter(prefs.reportPrinter);
    }
  }, [user]);

  useEffect(() => {
    // Load printers from Electron
    (async () => {
      try {
        // @ts-ignore
        const list = await (window as any).electronAPI?.getPrinters?.();
        if (Array.isArray(list)) setPrinters(list);
      } catch {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    setIsDesktop(isElectronApp());
  }, []);

  useEffect(() => {
    const api = (window as any).electronAPI;
    if (!api?.getAppVersion) return;
    api.getAppVersion().then((v: string) => { setAppVersion(v); appVersionRef.current = v; }).catch(() => {});
  }, []);

  // Subscribe to update events ONCE. Listeners are removed on unmount via the
  // unsubscribe fns; re-subscribing on every render previously stacked listeners
  // and produced many duplicate toasts for a single event.
  useEffect(() => {
    const api = (window as any).electronAPI;
    if (!api) return;

    const onAvailable = (info: { version: string }) => {
      manualUpdateCheck.current = false;
      setCheckingUpdate(false);
      toast.info(`Update v${info.version} found — downloading in the background. Watch the bottom-right corner for progress.`);
    };
    const onNotAvailable = () => {
      if (manualUpdateCheck.current) {
        const v = appVersionRef.current;
        toast.info(v ? `You're on the latest available version (v${v}).` : 'No newer update is available.');
      }
      manualUpdateCheck.current = false;
      setCheckingUpdate(false);
    };
    const onError = (err: { message: string }) => {
      if (manualUpdateCheck.current) {
        toast.error(getErrorMessage(err, 'Update check failed'));
      }
      manualUpdateCheck.current = false;
      setCheckingUpdate(false);
    };
    const onDownloaded = (info: { version: string }) => {
      toast.success(`v${info.version} downloaded — use "Restart & Install" in the bottom-right notification.`);
    };

    const unsubscribers: Array<(() => void) | undefined> = [
      api.onUpdateAvailable?.(onAvailable),
      api.onUpdateNotAvailable?.(onNotAvailable),
      api.onUpdateError?.(onError),
      api.onUpdateDownloaded?.(onDownloaded),
    ];

    return () => {
      unsubscribers.forEach((unsub) => { if (typeof unsub === 'function') unsub(); });
    };
  }, []);

  const handleCheckForUpdates = useCallback(async () => {
    const api = (window as any).electronAPI;
    if (!api?.checkForUpdate) {
      toast.warn('Updates are only available in the installed desktop app.');
      return;
    }
    manualUpdateCheck.current = true;
    setCheckingUpdate(true);
    toast.info('Checking for updates…');
    // The outcome toast is emitted ONCE by the event handlers above
    // (onUpdateAvailable / onUpdateNotAvailable / onUpdateError), which are the
    // single source of truth. We only handle a hard IPC failure here so we don't
    // double-toast the same result.
    try {
      const result = await api.checkForUpdate();
      if (result && result.success === false) {
        manualUpdateCheck.current = false;
        setCheckingUpdate(false);
        toast.error(result.error || 'Update check failed');
      }
    } catch {
      manualUpdateCheck.current = false;
      setCheckingUpdate(false);
    }
  }, []);

  const applyTheme = (t: Theme) => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    if (t === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      root.classList.add(systemTheme);
      root.setAttribute('data-theme', systemTheme);
    } else {
      root.classList.add(t);
      root.setAttribute('data-theme', t);
    }
    // Also set on body for better compatibility
    document.body.classList.remove('light', 'dark');
    document.body.classList.add(
      t === 'system'
        ? window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light'
        : t
    );
  };

  const applyFontSize = (size: FontSize) => {
    const root = document.documentElement;
    root.classList.remove('text-sm', 'text-base', 'text-lg');
    switch (size) {
      case 'sm':
        root.classList.add('text-sm');
        root.style.fontSize = '14px';
        break;
      case 'md':
        root.classList.add('text-base');
        root.style.fontSize = '16px';
        break;
      case 'lg':
        root.classList.add('text-lg');
        root.style.fontSize = '18px';
        break;
    }
  };

  const save = async () => {
    setSaving(true);

    // Apply changes immediately
    applyTheme(theme);
    applyFontSize(fontSize);

    const updated = {
      ...user!,
      preferences: {
        reportLanguage: language,
        theme,
        fontSize,
        slipPrinter,
        reportPrinter,
      },
    } as any;

    setUser(updated);
    // Persist theme for next boot
    window.localStorage.setItem('hria-theme', theme);

    // Persist to localStorage for now (user-specific on this machine)
    const prefs = {
      reportLanguage: language,
      theme,
      fontSize,
      slipPrinter,
      reportPrinter,
    };
    window.localStorage.setItem('hria-prefs', JSON.stringify(prefs));

    setSaving(false);
    toast.success('Preferences saved successfully!');
  };

  // Apply theme when component mounts or theme changes
  useEffect(() => {
    const applyThemeToDocument = (t: Theme) => {
      const root = document.documentElement;
      root.classList.remove('light', 'dark');
      if (t === 'system') {
        const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        root.classList.add(systemTheme);
        root.setAttribute('data-theme', systemTheme);
      } else {
        root.classList.add(t);
        root.setAttribute('data-theme', t);
      }
      // Also set on body for better compatibility
      document.body.classList.remove('light', 'dark');
      document.body.classList.add(
        t === 'system'
          ? window.matchMedia('(prefers-color-scheme: dark)').matches
            ? 'dark'
            : 'light'
          : t
      );
    };

    applyThemeToDocument(theme);
  }, [theme]);

  // Apply font size when component mounts or fontSize changes
  useEffect(() => {
    const applyFontSizeToDocument = (size: FontSize) => {
      const root = document.documentElement;
      root.classList.remove('text-sm', 'text-base', 'text-lg');
      switch (size) {
        case 'sm':
          root.classList.add('text-sm');
          root.style.fontSize = '14px';
          break;
        case 'md':
          root.classList.add('text-base');
          root.style.fontSize = '16px';
          break;
        case 'lg':
          root.classList.add('text-lg');
          root.style.fontSize = '18px';
          break;
      }
    };

    applyFontSizeToDocument(fontSize);
  }, [fontSize]);

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <SettingsIcon className="h-8 w-8 text-primary-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600">Configure application preferences and permissions</p>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'preferences' && (
        <div className="card">
          <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="form-label">Report Language</label>
              <select
                className="form-input"
                value={language}
                onChange={(e) => setLanguage(e.target.value as Language)}
              >
                <option value="en">English</option>
                <option value="ur">Urdu</option>
              </select>
            </div>
            <div>
              <label className="form-label">Theme</label>
              <select
                className="form-input"
                value={theme}
                onChange={(e) => {
                  const t = e.target.value as Theme;
                  setTheme(t);
                }}
              >
                <option value="system">System</option>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </div>
            <div>
              <label className="form-label">Font Size</label>
              <select
                className="form-input"
                value={fontSize}
                onChange={(e) => setFontSize(e.target.value as FontSize)}
              >
                <option value="sm">Small</option>
                <option value="md">Medium</option>
                <option value="lg">Large</option>
              </select>
            </div>
            <div>
              <label className="form-label">Slip Printer</label>
              <select
                className="form-input"
                value={slipPrinter}
                onChange={(e) => setSlipPrinter(e.target.value)}
              >
                <option value="">Default</option>
                {printers.map((p: any) => (
                  <option key={p.name} value={p.name}>
                    {p.displayName || p.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Report Printer</label>
              <select
                className="form-input"
                value={reportPrinter}
                onChange={(e) => setReportPrinter(e.target.value)}
              >
                <option value="">Default</option>
                {printers.map((p: any) => (
                  <option key={p.name} value={p.name}>
                    {p.displayName || p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2 border-t border-gray-200 dark:border-gray-700 pt-4 mt-2">
              <label className="form-label">Application</label>
              {!isDesktop ? (
                <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-100">
                  <p className="font-semibold">Web browser mode</p>
                  <p className="mt-1">
                    This tab is the dev/prod <strong>website</strong>, not the installed desktop app.
                    Printers, auto-updates, and donation recording require the Electron installer.
                  </p>
                  <p className="mt-2">
                    <strong>Beta desktop</strong> uses the dev API ({' '}
                    <code className="text-xs">dev.app.hria.edu.pk</code>) and dev database.
                    <strong> Stable desktop</strong> uses prod ({' '}
                    <code className="text-xs">app.hria.edu.pk</code>).
                  </p>
                  <p className="mt-2">
                    <a
                      href="https://app.hria.edu.pk/downloads"
                      className="font-medium underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Download the beta desktop installer
                    </a>
                    , then check for updates here inside that app (not in the browser).
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Runtime: <strong>Desktop app</strong>
                </p>
              )}
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Installed version: <strong>{appVersion || (isDesktop ? '…' : '— (web browser)')}</strong>
                {appVersion && !appVersion.includes('beta') && (
                  <span className="block mt-1 text-amber-600 dark:text-amber-400">
                    Beta updates (e.g. 1.2.0-beta.0) require the Beta build from{' '}
                    <a href="https://app.hria.edu.pk/downloads" className="underline" target="_blank" rel="noreferrer">
                      app.hria.edu.pk/downloads
                    </a>
                    . After that, auto-update will notify you here.
                  </span>
                )}
              </p>
              <Button
                type="button"
                variant="secondary"
                onClick={handleCheckForUpdates}
                loading={checkingUpdate}
                leftIcon={<Download className="h-4 w-4" />}
              >
                Check for updates now
              </Button>
            </div>
          </div>
          <div className="card-footer flex justify-end">
            <Button
              onClick={save}
              loading={saving}
              leftIcon={<Save className="h-4 w-4" />}
            >
              Save Settings
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;